/**
 * IgnoreRulesSection
 * Collapsible section showing all active ignore rules with edit/delete actions.
 * Hidden when rules array is empty (caller is responsible for conditional render).
 */

import { useState } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import type { IgnoreRule } from '../../api/external-payments-hub.api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type RuleField = 'description' | 'counterparty' | 'merchantName' | 'paymentChannel';
type RuleOperator = 'contains' | 'starts_with' | 'exact' | 'equals';

export interface IgnoreRulesSectionProps {
  rules: IgnoreRule[];
  onDelete: (ruleId: string) => Promise<void>;
  onEdit: (
    ruleId: string,
    patch: Partial<Pick<IgnoreRule, 'field' | 'operator' | 'value'>>
  ) => Promise<void>;
  isLoading: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FIELD_LABELS: Record<string, string> = {
  description: 'Description',
  counterparty: 'Counterparty',
  merchantName: 'Merchant Name',
  paymentChannel: 'Payment Channel',
};

const OPERATOR_LABELS: Record<string, string> = {
  contains: 'contains',
  starts_with: 'starts with',
  exact: 'exactly matches',
  equals: 'equals',
};

const ALL_OPERATORS: RuleOperator[] = ['contains', 'starts_with', 'exact', 'equals'];
const ALL_FIELDS: RuleField[] = ['description', 'counterparty', 'merchantName', 'paymentChannel'];

// ---------------------------------------------------------------------------
// RuleRow
// ---------------------------------------------------------------------------

interface RuleRowProps {
  rule: IgnoreRule;
  onDelete: (ruleId: string) => Promise<void>;
  onEdit: (
    ruleId: string,
    patch: Partial<Pick<IgnoreRule, 'field' | 'operator' | 'value'>>
  ) => Promise<void>;
  isLoading: boolean;
}

function RuleRow({ rule, onDelete, onEdit, isLoading }: RuleRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [editField, setEditField] = useState<RuleField>(rule.field);
  const [editOperator, setEditOperator] = useState<RuleOperator>(rule.operator);
  const [editValue, setEditValue] = useState(rule.value);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleDeleteClick = () => {
    setDeleteConfirm(true);
    setIsEditing(false);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    setActionError(null);
    try {
      await onDelete(rule.id);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete rule');
      setIsDeleting(false);
      setDeleteConfirm(false);
    }
  };

  const handleEditSave = async () => {
    setActionError(null);
    try {
      await onEdit(rule.id, { field: editField, operator: editOperator, value: editValue });
      setIsEditing(false);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update rule');
    }
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditField(rule.field);
    setEditOperator(rule.operator);
    setEditValue(rule.value);
    setActionError(null);
  };

  return (
    <div
      className="rounded-lg border border-border bg-card p-3"
      data-testid={`ignore-rule-${rule.id}`}
    >
      {!isEditing ? (
        <>
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-foreground">
                <span className="font-medium">{FIELD_LABELS[rule.field] ?? rule.field}</span>
                {' '}
                <span className="text-muted-foreground">{OPERATOR_LABELS[rule.operator] ?? rule.operator}</span>
                {' '}
                <span className="font-medium">"{rule.value}"</span>
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                onClick={() => {
                  setIsEditing(true);
                  setDeleteConfirm(false);
                  setActionError(null);
                }}
                disabled={isLoading || isDeleting}
                data-testid={`ignore-rule-edit-btn-${rule.id}`}
                aria-label={`Edit ignore rule`}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                onClick={handleDeleteClick}
                disabled={isLoading || isDeleting}
                data-testid={`ignore-rule-delete-btn-${rule.id}`}
                aria-label={`Delete ignore rule`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {deleteConfirm && (
            <div className="mt-2 flex items-center gap-2">
              <p className="text-xs text-muted-foreground flex-1">
                Delete this rule? Transactions matching it will reappear.
              </p>
              <Button
                size="sm"
                variant="destructive"
                className="h-7 px-2.5 text-xs"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                data-testid={`ignore-rule-delete-confirm-${rule.id}`}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2.5 text-xs"
                onClick={() => setDeleteConfirm(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
            </div>
          )}
        </>
      ) : (
        /* Inline edit form */
        <div className="flex flex-col gap-2">
          <div className="flex flex-col sm:flex-row gap-2">
            <select
              className="flex-1 rounded-md border border-input bg-background px-2 py-1.5 text-base md:text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              value={editField}
              onChange={(e) => setEditField(e.target.value as RuleField)}
              aria-label="Rule field"
            >
              {ALL_FIELDS.map((f) => (
                <option key={f} value={f}>
                  {FIELD_LABELS[f]}
                </option>
              ))}
            </select>
            <select
              className="flex-1 rounded-md border border-input bg-background px-2 py-1.5 text-base md:text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              value={editOperator}
              onChange={(e) => setEditOperator(e.target.value as RuleOperator)}
              aria-label="Rule operator"
            >
              {ALL_OPERATORS.map((op) => (
                <option key={op} value={op}>
                  {OPERATOR_LABELS[op]}
                </option>
              ))}
            </select>
            <Input
              type="text"
              className="flex-1 border-input bg-background px-2 py-1.5 text-foreground md:text-xs"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              aria-label="Rule value"
            />
          </div>
          {actionError && (
            <p className="text-xs text-destructive" role="alert">
              {actionError}
            </p>
          )}
          <div className="flex items-center gap-2 justify-end">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2.5 text-xs"
              onClick={handleEditCancel}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="h-7 px-2.5 text-xs"
              onClick={handleEditSave}
              disabled={!editValue.trim()}
            >
              Save
            </Button>
          </div>
        </div>
      )}

      {actionError && !isEditing && (
        <p className="text-xs text-destructive mt-1.5" role="alert">
          {actionError}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

export function IgnoreRulesSection({ rules, onDelete, onEdit, isLoading }: IgnoreRulesSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (rules.length === 0) return null;

  return (
    <div data-testid="hub-section-ignore-rules">
      <button
        type="button"
        className="flex items-center justify-between w-full text-left"
        onClick={() => setIsExpanded((v) => !v)}
        aria-expanded={isExpanded}
      >
        <h2 className="text-sm font-semibold text-foreground m-0">
          Ignore Rules
          <span className="ml-2 text-xs font-medium text-muted-foreground bg-muted/30 border border-border px-1.5 py-0.5 rounded">
            {rules.length}
          </span>
        </h2>
        <span className="text-xs text-muted-foreground">
          {isExpanded ? 'Hide' : 'Show'}
        </span>
      </button>

      {isExpanded && (
        <div className="mt-2 flex flex-col gap-2">
          {rules.map((rule) => (
            <RuleRow
              key={rule.id}
              rule={rule}
              onDelete={onDelete}
              onEdit={onEdit}
              isLoading={isLoading}
            />
          ))}
        </div>
      )}
    </div>
  );
}
